import { Box, IconButton, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import { GeneralTableBox, HeadTableCell, RowTableCell, NoDataSkeleton, CustomButton } from '../../../../../common';
import { useState } from 'react';
import apiClient from '../../../../../hooks/apiClient';
import { exportInvoicesToExcel } from '../../../utils/exportInvoicesExcel';
import { formatDate } from '../../../../../utils/formatUtils';

/**
 * Tab de facturas del contrato
 * Muestra todas las facturas históricas del contrato con opción de descarga
 */
const InvoicesTab = ({ localContract }) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getReceiptNumber = (invoice) => {
    const typeLetter = invoice.type_letter || '-';
    const salePoint = invoice.sale_point_number
      ? String(invoice.sale_point_number).padStart(4, '0')
      : '----';
    const receiptNumber =
      invoice.afip_number && invoice.afip_number !== 0
        ? String(invoice.afip_number).padStart(8, '0')
        : invoice.receipt_number
        ? String(invoice.receipt_number).padStart(8, '0')
        : '--------';

    return `${typeLetter}-${salePoint}-${receiptNumber}`;
  };

  const handleDownload = async (invoiceUuid) => {
    try {
      setDownloadingId(invoiceUuid);
      const response = await apiClient.get(`invoices/print/${invoiceUuid}`);

      const base64Data = response.data.base64_pdf;
      const fileName = response.data.file_name;

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar la factura:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleExportExcel = async () => {
    if (!invoices || invoices.length === 0) return;
    
    try {
      setIsExportingExcel(true);
      const contractInfo = {
        contract_number: localContract.contract_number,
        subscriber_name: localContract.subscriber_name,
        plan_name: localContract.plan_name,
      };
      
      const filename = `Facturas_${localContract.contract_number || 'Contrato'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      await exportInvoicesToExcel(invoices, contractInfo, filename);
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const invoices = localContract.invoices || [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Facturas del Contrato
        </Typography>
        {invoices.length > 0 && (
          <CustomButton
            variant="contained"
            startIcon={<TableChartIcon />}
            onClick={handleExportExcel}
            disabled={isExportingExcel}
            size="small"
          >
            {isExportingExcel ? 'Exportando...' : 'Exportar Excel'}
          </CustomButton>
        )}
      </Box>

      <GeneralTableBox>
        <Table aria-label="invoices table">
          <TableHead>
            <TableRow>
              <HeadTableCell>Nro. Comprobante</HeadTableCell>
              <HeadTableCell>Fecha de Emisión</HeadTableCell>
              <HeadTableCell>Monto Neto</HeadTableCell>
              <HeadTableCell>IVA</HeadTableCell>
              <HeadTableCell>Monto Total</HeadTableCell>
              <HeadTableCell>Descargar</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.length > 0 ? (
              invoices.map((invoice, index) => (
                <TableRow key={index}>
                  <RowTableCell>{getReceiptNumber(invoice)}</RowTableCell>
                  <RowTableCell>{formatDate(invoice.entry_date)}</RowTableCell>
                  <RowTableCell>{formatAmount(invoice.to_net)}</RowTableCell>
                  <RowTableCell>{formatAmount(invoice.to_iva)}</RowTableCell>
                  <RowTableCell>{formatAmount(invoice.to_total)}</RowTableCell>
                  <RowTableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleDownload(invoice.uuid)}
                      disabled={downloadingId === invoice.uuid}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </RowTableCell>
                </TableRow>
              ))
            ) : (
              <NoDataSkeleton colSpan={6} message="No hay facturas para este contrato" />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>
    </Box>
  );
};

export default InvoicesTab;
